import { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import CartDrawer from './components/CartDrawer';
import Header from './components/Header';
import { LuxeAssistant } from './components/LuxeAssistant';
import { SiteGate } from './components/SiteGate';
import Footer from './components/Footer';
import SeoHead from './components/SeoHead';
import { Toaster } from './components/ui/toaster';

import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import FAQ from './pages/FAQ';
import Shipping from './pages/Shipping';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import Calculator from './pages/Calculator';
import Library from './pages/Library';
import Reviews from './pages/Reviews';
import Account from './pages/Account';
import GiftCards from './pages/GiftCards';
import BlackFriday from './pages/BlackFriday';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import CrewPortal from './pages/CrewPortal';

// Auto-refresh when a new build is deployed (keeps installed PWAs current).
function AutoUpdate() {
  useEffect(() => {
    let current = null;
    const bundleRe = /static\/js\/main\.[a-z0-9]+\.js/;
    const canReload = () => !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
    const check = async (force) => {
      try {
        const html = await (await fetch('/index.html', { cache: 'no-store' })).text();
        const m = html.match(bundleRe);
        if (!m) return;
        if (current === null) {
          const s = [...document.scripts].map((x) => x.src).find((x) => bundleRe.test(x));
          current = s ? s.match(bundleRe)[0] : m[0];
        }
        if (m[0] !== current && (force || canReload())) window.location.reload();
      } catch (e) {}
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(true); };
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(() => check(false), 5 * 60 * 1000);
    check(false);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin') || pathname.startsWith('/crew');
  if (isAdmin) {
    return (
      <div className="App">
        <main>{children}</main>
      </div>
    );
  }
  return (
    <SiteGate>
      <div className="App site-luxe">
        <Header />
        <main>{children}</main>
        <Footer />
        <LuxeAssistant />
      </div>
    </SiteGate>
  );
}

function App() {
  return (
    <CartProvider>
      <BrowserRouter>
        <AutoUpdate />
        <ScrollToTop />
        <SeoHead />
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/account" element={<Account />} />
            <Route path="/gift-cards" element={<GiftCards />} />
            <Route path="/black-friday" element={<BlackFriday />} />
            <Route path="/library" element={<Library />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/crew" element={<CrewPortal />} />
          </Routes>
        </Layout>
        <CartDrawer />
        <Toaster />
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
